import { stripInlineMd } from "utils/inlineMarkdown";

/**
 * JSON-LD schema builders for SSR'd marketing pages.
 *
 * These helpers return plain objects that get stringified into a
 * <script type="application/ld+json"> tag in the rendered <head>.
 */

const STIPPL_SOCIAL_LINKS = [
	"https://www.instagram.com/stippl.io/",
	"https://www.tiktok.com/@stippl.io",
	"https://www.facebook.com/stippl/",
];

const STIPPL_LOGO = "/android-chrome-512x512.png";

export interface BreadcrumbItem {
	name: string;
	url: string;
}

export function organizationLd(baseUrl: string): object {
	return {
		"@context": "https://schema.org",
		"@type": "Organization",
		name: "Stippl",
		url: baseUrl,
		logo: `${baseUrl}${STIPPL_LOGO}`,
		sameAs: STIPPL_SOCIAL_LINKS,
		contactPoint: [
			{
				"@type": "ContactPoint",
				email: "info@stippl.io",
				contactType: "customer support",
			},
		],
	};
}

/**
 * The platforms the product as a whole runs on. Pages for a feature that does not ship on all
 * three must pass their own `operatingSystem` — see the /esim route.
 */
const DEFAULT_OPERATING_SYSTEM = "Web, iOS, Android";

// Real store URLs, deliberately -- not an oversight and not the same rule as every clickable
// download CTA on the site (123yb6937h3, which is why those now all resolve an AppsFlyer OneLink
// via useAppDownloadUrl instead of a direct link). Schema.org's `downloadUrl` on a
// SoftwareApplication card is consumed by search engines and must name the canonical store
// listing; a OneLink here would be wrong and can break the rich result. These are bare canonical
// URLs on purpose -- the tracking-param variants that used to live in constants/appStoreLinks.ts
// (pt/ct, referrer) do not belong in a schema field, and that file has been deleted precisely so
// nothing can import a direct store URL for a clickable href again.
const APP_STORE_URL = "https://apps.apple.com/app/apple-store/id6443617088";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.stippl.stippl";
const DEFAULT_DOWNLOAD_URLS = [APP_STORE_URL, PLAY_STORE_URL];

/** Product-wide feature list, used by any page that does not pass a narrower one. */
const DEFAULT_FEATURE_LIST = [
	"AI trip planner",
	"Day-by-day itinerary builder",
	"Travel budget tracker",
	"Group expense splitter",
	"Packing list manager",
	"Shareable trip pages",
	"Travel reel generator",
	"Scratch map",
	"Travel journal",
	"Social travel feed",
];

export function softwareApplicationLd(opts: {
	baseUrl: string;
	name: string;
	description: string;
	screenshotUrl?: string;
	applicationCategory?: string;
	/**
	 * Opt-in, and deliberately not a default. Google expects `aggregateRating` to reflect a
	 * rating the page itself shows, so only pages that display one may emit it — today that is
	 * the homepage alone, out of the 20-odd routes that build a SoftwareApplication card.
	 *
	 * Whatever is passed here must match the visible copy. See HOME_RATING in marketingRoutes.tsx.
	 */
	aggregateRating?: { ratingValue: string; ratingCount: number };
	/**
	 * Defaults to the whole product's platforms. Pass a narrower value on pages whose feature is
	 * not on all three, so the card does not promise a surface the page cannot deliver.
	 */
	operatingSystem?: string;
	/** Defaults to the product-wide list. Pass a page-specific one where the generic list misleads. */
	featureList?: string[];
	/** Defaults to both stores. Narrow it alongside `operatingSystem` so the card stays consistent. */
	downloadUrl?: string[];
}): object {
	return {
		"@context": "https://schema.org",
		"@type": "SoftwareApplication",
		name: opts.name,
		description: opts.description,
		url: opts.baseUrl,
		applicationCategory: opts.applicationCategory || "TravelApplication",
		operatingSystem: opts.operatingSystem || DEFAULT_OPERATING_SYSTEM,
		featureList: opts.featureList || DEFAULT_FEATURE_LIST,
		downloadUrl: opts.downloadUrl || DEFAULT_DOWNLOAD_URLS,
		offers: {
			"@type": "Offer",
			price: "0",
			priceCurrency: "EUR",
		},
		...(opts.screenshotUrl ? { screenshot: opts.screenshotUrl } : {}),
		...(opts.aggregateRating
			? {
					aggregateRating: {
						"@type": "AggregateRating",
						ratingValue: opts.aggregateRating.ratingValue,
						ratingCount: opts.aggregateRating.ratingCount,
					},
				}
			: {}),
		publisher: {
			"@type": "Organization",
			name: "Stippl",
			url: "https://stippl.io",
		},
	};
}

export function productLd(opts: {
	baseUrl: string;
	name: string;
	description: string;
	tiers: { name: string; price: string; currency: string; description?: string }[];
}): object {
	return {
		"@context": "https://schema.org",
		"@type": "Product",
		name: opts.name,
		description: opts.description,
		brand: {
			"@type": "Brand",
			name: "Stippl",
		},
		offers: opts.tiers.map((tier) => ({
			"@type": "Offer",
			name: tier.name,
			price: tier.price,
			priceCurrency: tier.currency,
			...(tier.description ? { description: tier.description } : {}),
			availability: "https://schema.org/InStock",
		})),
	};
}

export function howToLd(opts: {
	name: string;
	description: string;
	steps: { name: string; text: string }[];
	totalTimeISO?: string;
}): object {
	return {
		"@context": "https://schema.org",
		"@type": "HowTo",
		name: opts.name,
		description: opts.description,
		...(opts.totalTimeISO ? { totalTime: opts.totalTimeISO } : {}),
		step: opts.steps.map((s, idx) => ({
			"@type": "HowToStep",
			position: idx + 1,
			name: s.name,
			text: s.text,
		})),
	};
}

/**
 * JSON-LD carries plain strings, so the dossier's inline markdown has to come off here.
 *
 * The guide FAQs are written with `**bold**` and the page renders it — `RichText` turns
 * `**Gimpo**` into `<strong>Gimpo</strong>`. The schema got the same string unrendered, so
 * South Korea's answer reached Google as *"Fly, from **Gimpo** inside Seoul"*, asterisks and
 * all. 93 answers across 48 geos.
 *
 * Stripped here rather than in the exporter, which is where it looks like it belongs: the DOM
 * and the schema read the same field, so removing the markers upstream would take the bold off
 * the page too. The render keeps them; only the schema loses them.
 *
 * Google and ChatGPT read this block rather than the DOM, which is what makes it worth fixing
 * — the markup a reader never sees is the copy the answer engines quote.
 */
export function faqPageLd(faqs: { question: string; answer: string }[], name = "Frequently Asked Questions"): object {
	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		name,
		mainEntity: faqs.map((faq) => ({
			"@type": "Question",
			name: stripInlineMd(faq.question),
			acceptedAnswer: {
				"@type": "Answer",
				text: stripInlineMd(faq.answer),
			},
		})),
	};
}

/**
 * An Article's `author` — the house Organization, or a named Person.
 *
 * Guides have no byline at all and used to emit `{"@type":"Person","name":"Stippl"}` — an
 * organisation mismarked as a person, which is its own E-E-A-T problem (a "Person" schema.org
 * expects a human, with worksFor, not a company). Blog articles with a real writer —
 * `authorByline.ts`'s five real colleagues, never the house placeholder — get a genuine Person,
 * with `worksFor` linking back to Stippl.
 *
 * No author `url` on the Person: there are no author bio pages on the site, and a URL invented
 * for the field would be worse than the field's plain absence.
 */
function articleAuthorLd(baseUrl: string, authorName?: string, authorRegion?: string): object {
	const name = authorName?.trim();
	// "Stippl", "The Stippl editors": the house byline, not a person.
	if (!name || /\bstippl\b/i.test(name)) {
		return { "@type": "Organization", name: "Stippl", url: baseUrl };
	}
	return {
		"@type": "Person",
		name,
		...(authorRegion ? { knowsAbout: authorRegion } : {}),
		worksFor: { "@type": "Organization", name: "Stippl", url: baseUrl },
	};
}

export function articleLd(opts: {
	baseUrl: string;
	pageUrl: string;
	headline: string;
	description?: string;
	image?: string;
	imageWidth?: number;
	imageHeight?: number;
	/**
	 * Multiple aspect-ratio crops (16:9, 4:3, 1:1), all >= 1200px wide — Google Discover
	 * eligibility (see `discoverImageVariants` in `./discoverImages.ts`). When given, this
	 * REPLACES the single `image`/`imageWidth`/`imageHeight` shape below rather than adding to
	 * it: Article.image is either one image or an array, never a mix of both forms.
	 */
	images?: string[];
	datePublished?: string;
	dateModified?: string;
	authorName?: string;
	/**
	 * The region a byline covers (e.g. "Asia & Oceania" — `authorByline.ts`'s `Byline.role`, which the
	 * knowledge base fills from `website/authors.md`'s region heading). A region, not a job, so it goes
	 * out as the Person's `knowsAbout` rather than `jobTitle`. Ignored for the house Organization author.
	 */
	authorRegion?: string;
	articleBody?: string;
}): object {
	return {
		"@context": "https://schema.org",
		"@type": "Article",
		headline: opts.headline,
		...(opts.description ? { description: opts.description } : {}),
		...(opts.images?.length
			? { image: opts.images }
			: opts.image
				? {
						image:
							opts.imageWidth && opts.imageHeight
								? { "@type": "ImageObject", url: opts.image, width: opts.imageWidth, height: opts.imageHeight }
								: opts.image,
					}
				: {}),
		...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
		...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
		author: articleAuthorLd(opts.baseUrl, opts.authorName, opts.authorRegion),
		publisher: {
			"@type": "Organization",
			name: "Stippl",
			logo: {
				"@type": "ImageObject",
				url: `${opts.baseUrl}${STIPPL_LOGO}`,
			},
		},
		mainEntityOfPage: {
			"@type": "WebPage",
			"@id": opts.pageUrl,
		},
		...(opts.articleBody ? { articleBody: opts.articleBody.slice(0, 5000) } : {}),
	};
}

export function blogLd(opts: { baseUrl: string; description: string }): object {
	return {
		"@context": "https://schema.org",
		"@type": "Blog",
		name: "Stippl Blog",
		description: opts.description,
		url: `${opts.baseUrl}/blog`,
		publisher: {
			"@type": "Organization",
			name: "Stippl",
			url: opts.baseUrl,
		},
	};
}

export function breadcrumbLd(items: BreadcrumbItem[]): object {
	return {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: items.map((item, idx) => ({
			"@type": "ListItem",
			position: idx + 1,
			name: item.name,
			item: item.url,
		})),
	};
}

/**
 * `CollectionPage` wrapping an `ItemList` — the shape for a hub page whose whole purpose is the
 * list it carries (`/discover`, 123yb6937h0).
 *
 * `BreadcrumbList` (above) says where a page sits; this says what it holds. The two are emitted
 * side by side in `discoverHubHead` rather than one instead of the other.
 *
 * `url` per item, not a nested object per country: the guide pages each have their own full
 * `Place`/`Article` markup already, and repeating a thin copy of it here would give Google two
 * descriptions of the same entity from two pages. A bare `url` points at the authoritative one.
 */
export function collectionPageLd(opts: {
	name: string;
	description: string;
	url: string;
	items: { name: string; url: string }[];
}): object {
	return {
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		name: opts.name,
		description: opts.description,
		url: opts.url,
		mainEntity: {
			"@type": "ItemList",
			numberOfItems: opts.items.length,
			itemListElement: opts.items.map((item, idx) => ({
				"@type": "ListItem",
				position: idx + 1,
				name: item.name,
				url: item.url,
			})),
		},
	};
}
